import { Component, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmService, confirmParagraphs } from '../../../services/confirm.service';
import { ModalScrollLockDirective } from '../../../directives/modal-scroll-lock.directive';

/**
 * The single in-app confirm/notice dialog. Mounted once in AppComponent;
 * everything drives it through ConfirmService.ask() / .notify().
 *
 * Reuses `.modal-backdrop` / `.modal-box`, which already carry the safe-area
 * padding and dvh sizing the rest of the app's modals rely on.
 *
 * Backdrop click and Escape both cancel — but only for a confirm. For a notice
 * there is nothing to cancel, so they simply close it (resolving true), which
 * is what an `alert()` did.
 */
@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule, ModalScrollLockDirective],
  template: `
    @if (confirm.request(); as r) {
      <div class="modal-backdrop" appModalScrollLock (click)="dismiss()">
        <div class="modal-box max-w-md confirm-box" (click)="$event.stopPropagation()"
             role="alertdialog" aria-modal="true" [attr.aria-label]="r.title">
          <div class="flex items-start gap-3">
            <div [class]="'confirm-icon ' + (r.tone === 'danger' ? 'is-danger' : 'is-normal')">
              <i [class]="'fas ' + r.icon"></i>
            </div>
            <div class="min-w-0 flex-1">
              <h3 class="confirm-title">{{ r.title }}</h3>
              @for (p of paragraphs(r.message); track $index) {
                <p class="confirm-text">{{ p }}</p>
              }
              @if (r.detail) {
                <p [class]="'confirm-detail ' + (r.tone === 'danger' ? 'is-danger' : '')">{{ r.detail }}</p>
              }
            </div>
          </div>

          <div class="confirm-actions">
            @if (!r.notice) {
              <button type="button" class="btn-secondary flex-1 justify-center min-h-[48px]"
                (click)="confirm.respond(false)">{{ r.cancelLabel }}</button>
            }
            <!-- Deliberately not autofocused: on a danger dialog that would
                 make Enter fire a live radio page. -->
            <button type="button"
              [class]="'flex-1 justify-center min-h-[48px] ' + (r.tone === 'danger' ? 'btn-danger-confirm' : 'btn-primary')"
              (click)="confirm.respond(true)">{{ r.confirmLabel }}</button>
          </div>
        </div>
      </div>
    }
  `,
})
export class ConfirmDialogComponent {
  paragraphs = confirmParagraphs;

  constructor(public confirm: ConfirmService) {}

  /** Backdrop / Escape. A notice has only an OK button, so dismissing it is
   *  the same as pressing OK; a confirm defaults to the safe answer. */
  dismiss(): void {
    const r = this.confirm.request();
    if (!r) return;
    this.confirm.respond(r.notice);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.dismiss(); }
}
